import type { PageRevisionFamilyMember } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'

export type PageEndpoint = Pick<
  PageRevisionFamilyMember,
  'bqDocumentId' | 'pageNum'
>

const endpointKey = (bqDocumentId: string, pageNum: number) =>
  `${bqDocumentId}\u0000${pageNum}`

const revKey = (m: PageRevisionFamilyMember) =>
  `${m.systemPage.entry.data.latest_revision ?? ''}\u0000${m.systemPage.entry.data.latest_revision_date ?? ''}`

/**
 * Canonicalizes each revision member to the lexicographically-smallest
 * `bqDocumentId` among its same-system-revision siblings (grouped on the
 * system `latest_revision` / `latest_revision_date`, not the user-editable
 * display fields) — the id the detail producer keys on. Duplicate documents of one revision collapse
 * to a single canonical member. A revision with one document is unchanged.
 */
export function canonicalizeRevisionMembers(
  members: readonly PageRevisionFamilyMember[]
): {
  canonicalMembers: PageRevisionFamilyMember[]
  canonicalDocumentId: (bqDocumentId: string) => string
  canonicalEndpoint: (bqDocumentId: string, pageNum: number) => PageEndpoint
} {
  const minByRev = new Map<string, string>()
  for (const m of members) {
    const key = revKey(m)
    const current = minByRev.get(key)
    if (current === undefined || m.bqDocumentId < current) {
      minByRev.set(key, m.bqDocumentId)
    }
  }
  const rawToCanonical = new Map<string, string>()
  for (const m of members) {
    rawToCanonical.set(
      m.bqDocumentId,
      minByRev.get(revKey(m)) ?? m.bqDocumentId
    )
  }
  const canonicalDocumentId = (id: string) => rawToCanonical.get(id) ?? id

  const canonicalByRev = new Map<string, PageEndpoint>()
  for (const m of members) {
    const key = revKey(m)
    if (!canonicalByRev.has(key) && m.bqDocumentId === minByRev.get(key)) {
      canonicalByRev.set(key, {
        bqDocumentId: m.bqDocumentId,
        pageNum: m.pageNum,
      })
    }
  }
  const canonicalByKey = new Map<string, PageEndpoint>()
  for (const m of members) {
    canonicalByKey.set(
      endpointKey(m.bqDocumentId, m.pageNum),
      canonicalByRev.get(revKey(m)) ?? {
        bqDocumentId: m.bqDocumentId,
        pageNum: m.pageNum,
      }
    )
  }
  const canonicalEndpoint = (
    bqDocumentId: string,
    pageNum: number
  ): PageEndpoint =>
    canonicalByKey.get(endpointKey(bqDocumentId, pageNum)) ?? {
      bqDocumentId,
      pageNum,
    }

  const emitted = new Set<string>()
  const canonicalMembers: PageRevisionFamilyMember[] = []
  for (const m of members) {
    if (canonicalDocumentId(m.bqDocumentId) !== m.bqDocumentId) continue
    const key = `${m.bqDocumentId}\u0000${m.pageNum}`
    if (emitted.has(key)) continue
    emitted.add(key)
    canonicalMembers.push(m)
  }
  return { canonicalMembers, canonicalDocumentId, canonicalEndpoint }
}

export type CanonicalRevisionModel = {
  /** One member per revision bucket, latest-first, labelled with display-preferred code/date. */
  canonicalMembers: PageRevisionFamilyMember[]
  /**
   * Maps any raw family (document, page) to its revision bucket's canonical
   * endpoint (min document_id); unknown inputs pass through unchanged.
   */
  canonicalEndpoint: (bqDocumentId: string, pageNum: number) => PageEndpoint
  /**
   * Sorted, unique raw document ids of every family member, siblings included.
   * Queried for diffs so a detail stored against a re-upload id is not dropped.
   */
  familyDocumentIds: string[]
  /**
   * `canonicalMembers` limited to revisions still in the display family;
   * drives every displayed comparison (default, picker, launcher). Ids and
   * pages stay raw-canonical.
   */
  familyMembers: PageRevisionFamilyMember[]
}

/**
 * Canonical ids come from the raw (pre-user-edit) members so they match the
 * backend's tags; display code/date are overlaid by `memberEntryId` so panes
 * show user-edited labels.
 */
export function buildCanonicalRevisionModel(
  rawMembers: readonly PageRevisionFamilyMember[],
  displayMembers: readonly PageRevisionFamilyMember[]
): CanonicalRevisionModel {
  const { canonicalMembers, canonicalDocumentId, canonicalEndpoint } =
    canonicalizeRevisionMembers(rawMembers)
  const displayByEntryId = new Map(
    displayMembers.map((m) => [m.memberEntryId, m])
  )
  const labelled = canonicalMembers.map((m) => {
    const display = displayByEntryId.get(m.memberEntryId)
    return display
      ? {
          ...m,
          revisionCode: display.revisionCode,
          revisionDate: display.revisionDate,
        }
      : m
  })
  const displayCanonicalIds = new Set(
    displayMembers.map((m) => canonicalDocumentId(m.bqDocumentId))
  )
  const familyMembers = labelled.filter((m) =>
    displayCanonicalIds.has(m.bqDocumentId)
  )
  return {
    canonicalMembers: labelled,
    canonicalEndpoint,
    familyDocumentIds: [
      ...new Set(rawMembers.map((m) => m.bqDocumentId)),
    ].sort(),
    familyMembers,
  }
}

export const EMPTY_CANONICAL_REVISION_MODEL: CanonicalRevisionModel = {
  canonicalMembers: [],
  canonicalEndpoint: (bqDocumentId, pageNum) => ({ bqDocumentId, pageNum }),
  familyDocumentIds: [],
  familyMembers: [],
}
